/**
 * Skloňování jmen pro OSLOVENÍ klienta (5. pád / vokativ).
 *
 * Používá se všude, kde appka píše klientovi: WhatsApp zprávy, servisní radar,
 * dotazník k hypotéce, referral stránka, mapa klientů.
 *
 * Tři vrstvy:
 *   1) výběr oslovovacího jména z pole `name` (tituly, pořadí, vietnamská jména),
 *   2) slovník ověřených tvarů (má přednost),
 *   3) pravidla české deklinace s BEZPEČNÝM SELHÁNÍM.
 *
 * ZÁSADA BEZPEČNÉHO SELHÁNÍ: když si engine tvarem není jistý, vrátí NOMINATIV
 * a označí výsledek jako `confident: false`. Volající pak může oslovení úplně
 * vynechat (Dobrý den,) místo trapného tvaru typu Olivěře / Dagmare / Sarahu.
 *
 * Slovenština: spisovná slovenština vokativ u osobních jmen nemá (zachovalý je
 * jen u pár slov typu pán/otec/Boh). Oslovuje se NOMINATIVEM — Dobrý deň, Peter.
 * Proto se pro lang="sk" jméno neskloňuje, jen se vybere správný token.
 */

/* ------------------------------------------------------------------ *
 * Pomocné funkce
 * ------------------------------------------------------------------ */

const SAMOHLASKY = "aáäeéěiíoóôuúůyý";

/** malá písmena + odstranění diakritiky (vč. vietnamského đ). */
function asciiFold(s) {
  return String(s == null ? "" : s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/ø/g, "o")
    .replace(/ł/g, "l");
}

/** Ořízne z okrajů vše, co není písmeno (závorky, tečky, čísla, emoji). */
function ocisti(token) {
  return String(token == null ? "" : token)
    .replace(/^[^\p{L}]+/u, "")
    .replace(/[^\p{L}]+$/u, "");
}

/**
 * Je to vůbec tvar jména? Písmena, případně spojená apostrofem nebo pomlčkou
 * (O'Brien, Jan-Petr, Nguyễn).
 *
 * `ocisti` čistí jen OKRAJE, takže vnitřek tokenu projde. Bez téhle kontroly
 * se z pole name se vstupem zvenčí („<script>alert(1)</script>") stal token
 * `script>alert` a z něj oslovení „Script>alerte". Do HTML e-mailu to sice
 * odchází escapované, ale do zprávy klientovi takový token nepatří vůbec.
 */
function jeTvarJmena(token) {
  return /^\p{L}+(?:[’'-]\p{L}+)*$/u.test(token);
}

/**
 * VERZÁLKY z DB (PETR) převede na Petr, ať výsledek není PETŘe.
 *
 * Pozor: aplikuje se na KAŽDÝ TOKEN ZVLÁŠŤ. Kdyby se testoval celý řetězec,
 * míchaný zápis „PETR Novák" (běžný po importu z cizího systému) by neprošel,
 * protože řetězec jako celek verzálkami není — a vzniklo by „RADOVANe".
 */
function srovnejVerzalky(s) {
  const pismena = s.replace(/[^\p{L}]/gu, "");
  if (pismena.length < 2) return s;
  if (pismena !== pismena.toUpperCase()) return s;
  if (pismena === pismena.toLowerCase()) return s; // žádná velká/malá varianta
  return s.toLowerCase().replace(/(^|[\s'’-])(\p{L})/gu, (m, pred, ch) => pred + ch.toUpperCase());
}

/**
 * Vypadá token na ČESKÉ PŘÍJMENÍ? Slouží k rozpoznání pořadí PŘÍJMENÍ JMÉNO
 * (Novotný Gustav), když křestní jméno není v seznamu.
 *
 * Používáme jen zakončení, která ŽÁDNÉ české křestní jméno nemá:
 *   • -ý / -á    → adjektivní příjmení (Novotný, Černá, Veselý, Krátký),
 *   • -ých/-ích  → Petrových, Dolních,
 *   • -ová       → přechýlené (Nováková).
 * Křestní jména končí na -a (Jana, Klára), nikdy na -á s čárkou.
 *
 * POZOR na délku samohlásky u -ích: Bedřich/Jindřich/Oldřich mají KRÁTKÉ i,
 * příjmení Dolních dlouhé í. Proto se to testuje na původním tvaru s
 * diakritikou, ne na foldu — jinak by z Bedřicha bylo „příjmení".
 */
function vypadaJakoPrijmeni(tokenRaw) {
  const dolni = String(tokenRaw).toLowerCase();
  if (/[ýá]$/.test(dolni)) return true;
  if (/[ýí]ch$/.test(dolni)) return true;
  const fold = asciiFold(tokenRaw);
  return /ych$/.test(fold) || /ova$/.test(fold);
}

/** První písmeno velké (jen když je malé) — DB často má jména minuskami. */
function velkePrvni(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ------------------------------------------------------------------ *
 * Seznamy
 * ------------------------------------------------------------------ */

/** Akademické a profesní tituly — nikdy se z nich nesmí stát oslovení. */
const TITULY = new Set([
  "ing", "bc", "bca", "mgr", "mga", "mudr", "mddr", "mvdr", "judr", "phdr", "rndr",
  "paeddr", "thdr", "thlic", "pharmdr", "dr", "doc", "prof", "akad", "dis", "mba",
  "msc", "bsc", "llm", "phd", "csc", "drsc", "lic", "arch", "et", "dipl", "med",
]);

/**
 * Šlechtické / cizí předložky ve jméně (van der Berg, von Neumann, de Silva).
 * Vyhazují se JEN když jsou v původním zápisu malým písmenem — vietnamské
 * střední jméno Văn / Thị se píše s velkým, takže se jich to netýká.
 */
const PREDLOZKY = new Set([
  "van", "von", "de", "da", "del", "della", "der", "den", "di", "du", "dos", "das",
  "ter", "af", "av", "zu", "bin", "ibn",
]);

/** Nejčastější vietnamská příjmení (fold bez diakritiky: Nguyễn → nguyen). */
const VIET_PRIJMENI = new Set([
  "nguyen", "tran", "le", "pham", "hoang", "huynh", "phan", "vu", "vo", "dang",
  "bui", "do", "ho", "ngo", "duong", "ly", "dinh", "truong", "luong", "lam",
  "dao", "doan", "cao", "chu", "chau", "thai", "thach", "ton", "tu", "uong",
  "vuong", "quach", "tang", "kieu", "khuc", "nong", "luu", "ta", "ma", "han",
  "hua", "nghiem", "ninh", "phung", "trinh", "mac", "giang", "banh",
]);

/**
 * Křestní jména, která česky NESKLOŇUJEME.
 * a) vietnamská křestní (Trung, Nam, Lan…) — jinak by vznikl paskvil Trungu/Name,
 * b) další asijská (Kim, Chen, Wei…),
 * c) ženská jména zakončená na souhlásku (Dagmar, Ester, Miriam…) — ta jsou
 *    v češtině nesklonná a fallback pro mužská jména by z nich udělal Dagmare.
 */
const NESKLONNA = new Set([
  // --- vietnamská křestní / středová jména ---
  "trung", "nam", "son", "long", "tuan", "hung", "dung", "tung", "quang", "viet",
  "kien", "lan", "van", "hoa", "nga", "ha", "yen", "trang", "huong", "phuong",
  "cuong", "nhung", "tam", "tien", "toan", "sang", "thang", "diep", "loan",
  "quyen", "dat", "khang", "phong", "duy", "tuyen", "xuan", "thuan", "hoan",
  // pozor: záměrně tu NENÍ "dan" — Dan je běžná česká zkrácenina (Dan → Dane)
  "an", "tan", "man", "hien", "huyen", "uyen", "nhan", "kim", "thuy",
  "thi", "hong", "cam",
  // -nh / -c / -i: nejběžnější vietnamská jména vůbec. Bez nich je fallback
  // označil za nejistá (Anh, Minh, Linh) nebo zmršil (Ngan → „Ngane").
  "anh", "minh", "linh", "thanh", "khanh", "binh", "vinh", "hanh", "oanh",
  "quynh", "ngan", "duc", "phuc", "ngoc", "loc", "bao", "huy", "hue", "hang",
  "lien", "thu", "tuyet", "mai", "hieu", "chi", "my", "vy", "nhi", "tho",
  "nhu", "trinh", "khoa", "khai",
  // --- další asijská jména ---
  "chen", "wang", "zhang", "liu", "yang", "huang", "zhao", "wei", "park", "lee",
  "choi", "kang", "tanaka", "sato", "yamamoto", "singh", "khan",
  // --- ženská jména na souhlásku (v češtině nesklonná) ---
  "dagmar", "ester", "miriam", "mirjam", "karin", "karen", "ingrid", "astrid",
  "sigrid", "nikol", "nicol", "rachel", "ruth", "rut", "judit", "edit", "doris",
  "iris", "carmen", "jasmin", "yasmin", "sharon", "isabel", "muriel", "mercedes",
  "agnes", "marlen", "madlen", "gudrun", "solveig", "ines", "dolores", "beatriz",
  "elen", "ellen", "helen", "gwen", "kristen", "megan", "marion", "vivian",
  "lilian", "susan", "ingeborg", "esther", "rachael", "nicole", "michele",
]);

/**
 * Ověřené vokativy (klíč = jméno bez diakritiky, malými).
 * Slovník má přednost před pravidly — sem patří všechno, kde pravidlo selhává
 * (pohybné -e: Pavel→Pavle, cizí vzory: Daniel→Danieli, měkčení: Zdeněk→Zdeňku).
 */
const VOCATIVE_OVERRIDES = {
  // --- muži: pohybné -e a nepravidelnosti ---
  pavel: "Pavle", karel: "Karle", havel: "Havle", petr: "Petře",
  jan: "Jane", honza: "Honzo", jirka: "Jirko", jakub: "Jakube", kuba: "Kubo",
  ales: "Aleši", tomas: "Tomáši", lukas: "Lukáši", david: "Davide",
  ondrej: "Ondřeji", matej: "Matěji", martin: "Martine", michal: "Michale",
  roman: "Romane", filip: "Filipe", patrik: "Patriku", marek: "Marku",
  mirek: "Mirku", radek: "Radku", zdenek: "Zdeňku", zbynek: "Zbyňku",
  ludek: "Luďku", vasek: "Vašku", milan: "Milane", libor: "Libore",
  radim: "Radime", richard: "Richarde", rostislav: "Rostislave",
  vaclav: "Václave", vladimir: "Vladimíre", vojtech: "Vojtěchu", adam: "Adame",
  jaroslav: "Jaroslave", josef: "Josefe", pepa: "Pepo", ladislav: "Ladislave",
  lubos: "Luboši", luboslav: "Luboslave", oldrich: "Oldřichu", otakar: "Otakare",
  rene: "René", robert: "Roberte", stanislav: "Stanislave", standa: "Stando",
  svatopluk: "Svatopluku", tibor: "Tibore", viktor: "Viktore", vit: "Víte",
  jiri: "Jiří", miroslav: "Miroslave", simon: "Šimone", antonin: "Antoníne",
  frantisek: "Františku", franta: "Franto", vratislav: "Vratislave",
  // --- muži: vzor Daniel (cizí jména na -el/-eus berou -i) ---
  daniel: "Danieli", gabriel: "Gabrieli", samuel: "Samueli", emanuel: "Emanueli",
  marcel: "Marceli", michael: "Michaeli", rafael: "Rafaeli", ariel: "Arieli",
  alex: "Alexi", denis: "Denisi", alois: "Aloisi", vavrinec: "Vavřinče",
  // --- ženy ---
  marie: "Marie", lucie: "Lucie", eva: "Evo", anna: "Anno", jana: "Jano",
  tereza: "Terezo", kristyna: "Kristýno", katerina: "Kateřino", klara: "Kláro",
  simona: "Simono", monika: "Moniko", lenka: "Lenko", petra: "Petro",
  martina: "Martino", veronika: "Veroniko", helena: "Heleno", hana: "Hano",
  pavla: "Pavlo", magdalena: "Magdaléno", natalie: "Natálie", sara: "Sáro",
  dana: "Dano", iveta: "Iveto", alena: "Aleno", barbora: "Barboro",
  michaela: "Michaelo", denisa: "Deniso", aneta: "Aneto", andrea: "Andreo",
  zuzana: "Zuzano", radka: "Radko", romana: "Romano", alice: "Alice",
  libuse: "Libuše", zofie: "Žofie", julie: "Julie", viktorie: "Viktorie",
  emilie: "Emílie", sofie: "Sofie", nikola: "Nikolo", adela: "Adélo",
  bozena: "Boženo", ludmila: "Ludmilo", jitka: "Jitko", sarka: "Šárko",
};

/**
 * Známá křestní jména — slouží k rozpoznání pořadí PŘÍJMENÍ JMÉNO
 * (DB často obsahuje Novák Petr). Nemá vliv na tvar, jen na výběr tokenu.
 */
const ZNAMA_KRESTNI = new Set([
  ...Object.keys(VOCATIVE_OVERRIDES),
  ...NESKLONNA,
  "kamil", "emil", "bohumil", "dominik", "erik", "ludvik", "jonas", "elias",
  "matyas", "mikulas", "milos", "miloslav", "bohuslav", "premysl", "kristian",
  "sebastian", "kryštof", "krystof", "norbert", "oliver", "igor", "artur",
  "dalibor", "ctibor", "teodor", "vilem", "cyril", "metodej", "bedrich",
  "vlastimil", "kevin", "hugo", "ivo", "otto", "bruno", "nikolas", "felix",
  "matous", "vaclav", "svatopluk", "borek", "slavek", "honzik", "jarda",
  "vojta", "mira", "ruda", "beda", "jarek", "dominika", "gabriela", "ivana",
  "irena", "ilona", "blanka", "vlasta", "kveta", "nadezda", "marcela",
  "zdenka", "anezka", "jarmila", "vendula", "kamila", "nikoleta", "elena",
  "bara", "ema", "nela", "karolina", "sylvie", "silvie", "renata", "olga",
  "sona", "vera", "milada", "jindriska", "stepanka", "eliska", "natalka",
  "lubica", "zlatica", "danica", "peter", "jozef", "miroslava", "milena",
  // Doplněno po probrání 60 nejčastějších jmen ČSÚ + běžných méně častých:
  // bez nich engine u zápisu „Sedlák Radovan" / „Horák Leopold" oslovil
  // klienta PŘÍJMENÍM („Dobrý den Sedláku,").
  "radovan", "leopold", "maxmilian", "gustav", "ctirad", "hubert", "bohdan",
  "kvetoslav", "slavomir", "blahoslav", "drahomir", "kvido", "lumir", "mojmir",
  "vlastislav", "milon", "jaromir", "stepan", "vladislav", "dusan", "prokop",
  "vilem", "premysl", "metodej", "hynek", "leos", "arnost", "bretislav",
  "evzen", "eduard", "jindrich", "lubomir", "milos", "matyas", "miloslav",
  "vilibald", "zdenek", "zbynek", "ludek", "vasek", "rudolf", "vratislav",
  "alexandr", "otakar", "viktor", "tibor", "libor", "ivan", "robert", "denis",
  "marcel", "rafael", "samuel", "gabriel", "emanuel", "ariel", "alex", "felix",
  "artur", "vojtech", "oldrich", "antonin", "ondrej", "matej", "michael",
  // ženská
  "marketa", "emma", "beata", "alzbeta", "pavlina", "sabina", "yvona", "judita",
  "nadezda", "kvetoslava", "jaroslava", "stanislava", "vladimira", "ladislava",
  "bohumila", "vlastimila", "zdenka", "veronika", "michaela", "magdalena",
  "barbora", "kristyna", "katerina", "tereza", "lucie", "denisa", "aneta",
  "adela", "nikola", "klara", "sara", "iveta", "sarka", "jitka", "libuse",
  "bozena", "ludmila", "helena", "hana", "dana", "alena", "andrea", "zuzana",
  "radka", "romana", "monika", "simona", "lenka", "petra", "martina", "pavla",
]);

/* ------------------------------------------------------------------ *
 * 1) Výběr oslovovacího jména z celého pole `name`
 * ------------------------------------------------------------------ */

function tokeny(fullName) {
  // Jen řetězec — objekt/číslo/boolean z rozbité DB nesmí skončit jako oslovení.
  if (typeof fullName !== "string") return [];
  const hrube = fullName
    .trim()
    .split(/[\s,;/]+/)
    .map(ocisti)
    .filter((t) => t.length > 0)
    .filter(jeTvarJmena)
    .filter((t) => !TITULY.has(asciiFold(t).replace(/[^a-z]/g, "")));
  // Předložky (van der Berg) se zahazují jen když jsou malým písmenem —
  // tohle se musí stát PŘED srovnáním verzálek, jinak se rozlišení ztratí.
  const bezPredlozek = hrube.filter(
    (t) => !(hrube.length > 1 && t === t.toLowerCase() && PREDLOZKY.has(asciiFold(t)))
  );
  const zbytek = bezPredlozek.length ? bezPredlozek : hrube;
  return zbytek.map(srovnejVerzalky); // srovnání VERZÁLEK token po tokenu
}

/**
 * Vybere token, kterým se má člověk oslovit.
 *
 * Vietnamské jméno má pořadí PŘÍJMENÍ – prostřední – KŘESTNÍ a oslovuje se
 * KŘESTNÍM, tedy posledním slovem (Nguyen Duc Trung → Trung). Zvládá i
 * pozápadněné pořadí (Trung Nguyen → Trung).
 *
 * Rozlišení proti českému zápisu PŘÍJMENÍ JMÉNO (Novák Petr) NENÍ obecně možné
 * z tvaru slova — děláme to podle SEZNAMŮ:
 *   • první/poslední token je vietnamské příjmení → vietnamské pořadí,
 *   • první token končí na -ová (Nováková Jana) → příjmení je první,
 *   • první token NENÍ známé křestní a druhý ANO (Novák Petr) → příjmení je první.
 * Když ani jedno neplatí, bereme první slovo. U neznámého jména mimo seznamy
 * (např. Sedlák Radovan) tedy zůstává původní chování — to je vědomý limit.
 */
function vyberOsloveni(toks) {
  if (!toks.length) return { token: "", nesklonne: true, duvod: "prazdne" };
  if (toks.length === 1) return { token: toks[0], nesklonne: false, duvod: "jedine-slovo" };

  const fold = toks.map(asciiFold);
  const prvni = fold[0];
  const posledni = fold[fold.length - 1];

  if (VIET_PRIJMENI.has(prvni)) {
    return { token: toks[toks.length - 1], nesklonne: true, duvod: "vietnamske-prijmeni-prvni" };
  }
  if (VIET_PRIJMENI.has(posledni) && !ZNAMA_KRESTNI.has(posledni)) {
    return { token: toks[0], nesklonne: true, duvod: "vietnamske-prijmeni-posledni" };
  }
  if (/ova$/.test(prvni) && !ZNAMA_KRESTNI.has(prvni)) {
    return { token: toks[1], nesklonne: false, duvod: "prijmeni-prvni-ova" };
  }
  if (!ZNAMA_KRESTNI.has(prvni) && ZNAMA_KRESTNI.has(fold[1])) {
    return { token: toks[1], nesklonne: false, duvod: "prijmeni-prvni-znamekrestni" };
  }
  // Adjektivní příjmení na prvním místě (Novotný Gustav, Černá Ludmila).
  // Žádné české křestní jméno nekončí na -ý/-á/-ých, takže je to jistý signál
  // i tehdy, když křestní jméno v seznamu nemáme.
  if (vypadaJakoPrijmeni(toks[0]) && !ZNAMA_KRESTNI.has(prvni)) {
    return { token: toks[1], nesklonne: false, duvod: "prijmeni-prvni-adjektivni" };
  }
  return { token: toks[0], nesklonne: false, duvod: "prvni-slovo" };
}

/* ------------------------------------------------------------------ *
 * 2) + 3) Vokativ jednoho tokenu
 * ------------------------------------------------------------------ */

const MEKCENI = { n: "ň", d: "ď", t: "ť" };

function vokativTokenu(token) {
  const key = asciiFold(token);

  if (NESKLONNA.has(key) || VIET_PRIJMENI.has(key)) {
    return { text: token, confident: true, rule: "nesklonne" };
  }
  if (VOCATIVE_OVERRIDES[key]) {
    return { text: VOCATIVE_OVERRIDES[key], confident: true, rule: "slovnik" };
  }
  if (token.length < 2) {
    return { text: token, confident: false, rule: "prilis-kratke" };
  }
  // Rozumný strop: žádné křestní jméno není delší než ~30 znaků. Delší řetězec
  // je rozbitá DB (slepený text, poznámka v poli name) — do zprávy nepatří.
  if (token.length > 30) {
    return { text: token, confident: false, rule: "prilis-dlouhe" };
  }

  const lastL = token.slice(-1).toLowerCase();
  const prevL = token.slice(-2, -1).toLowerCase();

  // Přechýlené příjmení (Nováková) i adjektivní (Novotný) — vokativ = nominativ.
  if (/ova$/.test(key)) {
    return { text: token, confident: true, rule: "prijmeni-ova" };
  }
  // Ženská (a některá mužská) jména na -a/-á → -o: Jana→Jano, Honza→Honzo.
  if (lastL === "a" || lastL === "á") {
    return { text: token.slice(0, -1) + "o", confident: true, rule: "a-na-o" };
  }
  // Zakončení na samohlásku se nemění: Marie, Jiří, René, Ivo, Nikolaj? (ne, ten je -j).
  if (SAMOHLASKY.includes(lastL) && lastL !== "a") {
    return { text: token, confident: true, rule: "samohlaska-beze-zmeny" };
  }
  // -ch → -u (Vojtěchu, Bedřichu). Samotné -h je v češtině u jmen cizí
  // (Sarah, Noah, Elizabeth) → radši nominativ než Sarahu.
  if (lastL === "h") {
    if (prevL === "c") return { text: token + "u", confident: true, rule: "ch-na-u" };
    return { text: token, confident: false, rule: "cizi-h-nejiste" };
  }
  // Měkké souhlásky → -i: Aleši, Tomáši, Ondřeji, Matěji.
  // Pozor na pravopis: před -i se háček nepíše (kůň → koni), takže
  // Květoň → Květoni, ne Květoňi.
  if ("šžčřjňťď".includes(lastL)) {
    const bezHacku = { "ň": "n", "ť": "t", "ď": "d" }[lastL];
    if (bezHacku) {
      return { text: token.slice(0, -1) + bezHacku + "i", confident: true, rule: "mekka-bez-hacku-i" };
    }
    return { text: token + "i", confident: true, rule: "mekka-souhlaska-i" };
  }
  // -k: nejdřív pohybné -e (Vašek→Vašku, Zdeněk→Zdeňku), pak prosté -u.
  if (lastL === "k") {
    const konec2 = token.slice(-2).toLowerCase();
    const zaklad = token.slice(0, -2);
    if ((konec2 === "ek" || konec2 === "ěk") && zaklad.length >= 2) {
      let z = zaklad;
      if (konec2 === "ěk") {
        const p = z.slice(-1).toLowerCase();
        if (MEKCENI[p]) z = z.slice(0, -1) + MEKCENI[p];
      }
      return { text: z + "ku", confident: true, rule: "ek-na-ku" };
    }
    return { text: token + "u", confident: true, rule: "k-na-u" };
  }
  // -r: po SOUHLÁSCE měkčí na -ře (Petr→Petře, Alexandr→Alexandře),
  //     po SAMOHLÁSCE jen -e (Igor→Igore, Oliver→Olivere, Otakar→Otakare).
  if (lastL === "r") {
    if (prevL && !SAMOHLASKY.includes(prevL)) {
      return { text: token.slice(0, -1) + "ře", confident: true, rule: "r-po-souhlasce-re" };
    }
    return { text: token + "e", confident: true, rule: "r-po-samohlasce-e" };
  }
  // -g → -u (pedagogu, Ludvigu).
  if (lastL === "g") {
    return { text: token + "u", confident: true, rule: "g-na-u" };
  }
  // -c: chlapec→chlapče, ale Švec→ševče (nepravidelné) a u příjmení se
  // kolísá (Kadlec). U křestních jmen je -c v podstatě jen Vavřinec (slovník).
  // Zbytek radši nominativ, ať nevznikne Vavřineci.
  if (lastL === "c") {
    return { text: token, confident: false, rule: "c-nejiste" };
  }
  // -s/-x/-z u cizích jmen → -i: Denisi, Aloisi, Alexi, Felixi.
  // Latinské -us (Julius, Klaus) má kolísavý tvar → nominativ.
  if ("sxz".includes(lastL)) {
    if (key.endsWith("us")) return { text: token, confident: false, rule: "latinske-us-nejiste" };
    return { text: token + "i", confident: true, rule: "s-x-z-na-i" };
  }
  // Tvrdé souhlásky → -e: Adame, Davide, Filipe, Martine, Michale, Václave.
  if ("bdflmnptv".includes(lastL)) {
    return { text: token + "e", confident: true, rule: "tvrda-souhlaska-e" };
  }
  // Cokoli jiného (w, q, cizí zakončení, znaky) — nehádáme.
  return { text: token, confident: false, rule: "nezname-zakonceni" };
}

/* ------------------------------------------------------------------ *
 * Veřejné API
 * ------------------------------------------------------------------ */

/**
 * Oslovení + příznak jistoty.
 *
 * @param {string} fullName  jméno z DB (klidně celé: tituly, příjmení, …)
 * @param {string} [lang="cs"]  skloňuje se JEN "cs"; ostatní dostanou 1. pád
 * @returns {{text: string, confident: boolean, rule: string}}
 *   confident=false znamená: NEPOUŽÍVEJ jméno v oslovení, napiš jen Dobrý den,
 */
export function toVocativeInfo(fullName, lang = "cs") {
  const toks = tokeny(fullName);
  const vybrano = vyberOsloveni(toks);
  if (!vybrano.token) return { text: "", confident: false, rule: "prazdne" };

  /* ČESKÝ VOKATIV PATŘÍ JEN DO ČEŠTINY.
   *
   * Do 8/2026 tu stálo `if (lang === "sk")` — tedy „skloňuj všude kromě
   * slovenštiny". Napsalo se to v době, kdy appka uměla jen cs+sk. Jenže
   * jazyky jsou dnes 4 + čeština, takže anglický, ukrajinský i vietnamský
   * dokument dostával ČESKÝ 5. pád: klientský výstup životka oslovoval
   * anglicky mluvícího Petra „Petře" (ověřeno vykreslením v pěti jazycích).
   *
   * Ukrajinština vokativ má, ale řídí se vlastními pravidly — dosadit do ní
   * české skloňování je stejná chyba jako u angličtiny. Dokud pro ni nemáme
   * doložená pravidla, dostane 1. pád: neutrální a vždycky správně. */
  if (lang !== "cs") {
    return { text: velkePrvni(vybrano.token), confident: true, rule: `${lang}-nominativ` };
  }
  if (vybrano.nesklonne) {
    return { text: velkePrvni(vybrano.token), confident: true, rule: vybrano.duvod };
  }
  const v = vokativTokenu(vybrano.token);
  return { text: velkePrvni(v.text), confident: v.confident, rule: v.rule };
}

/**
 * Vokativ (5. pád) jména. Zvládne i celé jméno — vybere si správný token.
 * Když si engine není jistý, vrací nominativ (viz toVocativeInfo).
 */
export function toVocative(fullName, lang = "cs") {
  return toVocativeInfo(fullName, lang).text;
}

/** Oslovení z celého jména v 5. pádě (CS) / nominativu (SK). */
export function firstNameVocative(fullName, lang = "cs") {
  return toVocativeInfo(fullName, lang).text;
}

/** Totéž jako firstNameVocative, ale i s příznakem jistoty. */
export function firstNameVocativeInfo(fullName, lang = "cs") {
  return toVocativeInfo(fullName, lang);
}

/**
 * Oslovení do ZPRÁVY KLIENTOVI — bezpečná varianta pro šablony.
 *
 * Vrací hotový tvar jen tehdy, když si jím engine je jistý. Jinak vrací
 * prázdný řetězec, aby šablona oslovení VYNECHALA („Dobrý den,") místo
 * vymyšleného tvaru („Dobrý den Olivěře,"). Tohle je API, které mají volat
 * všechny šablony zpráv (WhatsApp, e-mail, PDF, pozvánky).
 *
 * @returns {string} vokativ, nebo "" když jméno chybí / je nejisté
 */
export function osloveniProZpravu(fullName, lang = "cs") {
  const info = toVocativeInfo(fullName, lang);
  return info.confident ? info.text : "";
}

/**
 * Křestní jméno v 1. pádě — bez titulů, se správným výběrem u vietnamských
 * jmen (Nguyen Duc Trung → Trung) i u zápisu PŘÍJMENÍ JMÉNO (Novák Petr → Petr).
 */
export function firstName(fullName) {
  const vybrano = vyberOsloveni(tokeny(fullName));
  return velkePrvni(vybrano.token || "");
}

/** Jen pro testy a diagnostiku: proč byl vybrán ten který token. */
export function vyberOsloveniDebug(fullName) {
  return vyberOsloveni(tokeny(fullName));
}
